import { create } from 'zustand';
import type { AudioChunkRecord, FootageAsset, SignedMedia } from '../api/client';
import type { CaptureSegment, MicCaptureState, MicMode } from '../capture/recorder';

/**
 * Session state machine (implementation-plan.md §1.1). The feature cards drive transitions;
 * this store is the single source of truth so capture → review → render stays coherent.
 */
export type SessionStatus =
  | 'idle'
  | 'loading'
  | 'recording'
  | 'transcribing'
  | 'structuring'
  | 'review'
  | 'rendering'
  | 'complete'
  | 'error';

export interface ClipProposal {
  id: string;
  tIn: number;
  tOut: number;
  label: string;
  vocabRef: string | null;
  notes: string;
  confidence: number;
  status: 'proposed' | 'confirmed' | 'edited' | 'rejected' | 'rendered';
}

/** One line of the live transcript, already mapped onto the video timeline. */
export interface TranscriptSegmentView {
  text: string;
  startS: number;
  endS: number;
  /** Which capture segment it came from (null for API-sourced text). */
  segmentIndex?: number;
}

export type UploadState = 'idle' | 'probing' | 'uploading' | 'ready' | 'error';

export interface TransitionRecord {
  from: SessionStatus;
  to: SessionStatus;
  at: string;
  reason: string;
}

export interface SessionState {
  status: SessionStatus;
  error: string | null;
  footageId: string | null;
  sessionId: string | null;
  clips: ClipProposal[];

  /** Capture-card state */
  footage: FootageAsset | null;
  media: SignedMedia | null;
  uploadState: UploadState;
  uploadLoaded: number;
  uploadTotal: number;
  uploadError: string | null;
  segments: CaptureSegment[];
  transcriptSegments: TranscriptSegmentView[];
  micMode: MicMode;
  micState: MicCaptureState;
  micError: string | null;
  capabilityReport: unknown;
  /** Every state-machine transition this session made, in order (audit + E2E evidence). */
  transitionLog: TransitionRecord[];

  setStatus: (status: SessionStatus) => void;
  setError: (error: string | null) => void;
  setFootageId: (footageId: string) => void;
  setSession: (sessionId: string) => void;
  setClips: (clips: ClipProposal[]) => void;

  setFootage: (footage: FootageAsset, media: SignedMedia) => void;
  setUpload: (patch: Partial<Pick<SessionState, 'uploadState' | 'uploadLoaded' | 'uploadTotal' | 'uploadError'>>) => void;
  /** Upserts by segmentIndex: a segment is updated as its chunks land, never duplicated. */
  upsertSegment: (segment: CaptureSegment) => void;
  setSegments: (segments: CaptureSegment[]) => void;
  pushTranscriptSegment: (segment: TranscriptSegmentView) => void;
  setTranscriptSegments: (segments: TranscriptSegmentView[]) => void;
  setMicMode: (mode: MicMode) => void;
  setMicState: (state: MicCaptureState, error?: string | null) => void;
  setCapabilityReport: (report: unknown) => void;
  noteTransition: (record: TransitionRecord) => void;
  /** Clears session-scoped state but keeps the uploaded footage (start another session). */
  resetSession: () => void;
  reset: () => void;
}

const initial = {
  status: 'idle' as SessionStatus,
  error: null,
  footageId: null,
  sessionId: null,
  clips: [] as ClipProposal[],
  footage: null,
  media: null,
  uploadState: 'idle' as UploadState,
  uploadLoaded: 0,
  uploadTotal: 0,
  uploadError: null,
  segments: [] as CaptureSegment[],
  transcriptSegments: [] as TranscriptSegmentView[],
  micMode: 'open' as MicMode,
  micState: 'idle' as MicCaptureState,
  micError: null,
  capabilityReport: null as unknown,
  transitionLog: [] as TransitionRecord[],
};

export const useSessionStore = create<SessionState>((set) => ({
  ...initial,
  setStatus: (status) => set({ status }),
  setError: (error) => set({ error, status: error ? 'error' : 'idle' }),
  setFootageId: (footageId) => set({ footageId }),
  setSession: (sessionId) => set({ sessionId }),
  setClips: (clips) => set({ clips }),

  setFootage: (footage, media) =>
    set({ footage, media, footageId: footage.id, uploadState: 'ready', uploadError: null }),
  setUpload: (patch) => set(patch),
  upsertSegment: (segment) =>
    set((s) => {
      const index = s.segments.findIndex((existing) => existing.segmentIndex === segment.segmentIndex);
      if (index < 0) return { segments: [...s.segments, segment] };
      const next = [...s.segments];
      next[index] = segment;
      return { segments: next };
    }),
  setSegments: (segments) => set({ segments }),
  pushTranscriptSegment: (segment) =>
    set((s) => ({ transcriptSegments: [...s.transcriptSegments, segment] })),
  setTranscriptSegments: (transcriptSegments) => set({ transcriptSegments }),
  setMicMode: (micMode) => set({ micMode }),
  setMicState: (micState, micError = null) => set({ micState, micError }),
  setCapabilityReport: (capabilityReport) => set({ capabilityReport }),
  noteTransition: (record) => set((s) => ({ transitionLog: [...s.transitionLog, record] })),
  resetSession: () =>
    set({
      status: 'idle',
      error: null,
      sessionId: null,
      clips: [],
      uploadState: 'idle',
      uploadLoaded: 0,
      uploadTotal: 0,
      uploadError: null,
      segments: [],
      transcriptSegments: [],
      micState: 'idle',
      micError: null,
      transitionLog: [],
    }),
  reset: () => set({ ...initial }),
}));

/** Convenience for the E2E harness and the (PIPELINE-owned) transcription feed. */
export function transcriptFromChunks(chunks: AudioChunkRecord[]): TranscriptSegmentView[] {
  return chunks.map((chunk) => ({
    text: '',
    startS: 0,
    endS: 0,
    segmentIndex: chunk.chunkIndex,
  }));
}
